import { AssetResponse } from "../FetchAssetDetails/types";
import { CLIPS_INFO, NBA_CLIPS, DEFAULT_CLIPS, DEFAULT_COPY } from "./configs";

/**
 * Get the video URL, top/bottom line text, text color, and rating badge for a clip based on the asset title.
 *
 * @param asset - The asset to get clip info for
 * @returns The video URL, top/bottom line text, text color, and rating badge for the clipTile
 *
 * @example
 * ```typescript
 * const clipInfo = getCantMissClipsInfo(asset);
 * // Returns: {
 *   url: "https://customer-z5bebwk1lsy9rooc.cloudflarestream.com/1a5c005e92217e775503aaf46edd2278/manifest/video.m3u8",
 *   topLineText: "Traiterous",
 *   bottomLineText: "Gameplay",
 *   textColor: "#FCCC12",
 *   ratingBadge: "TVMA"
 * }
 * ```
 */
export const getCantMissClipsInfo = (
  asset: AssetResponse | undefined,
): {
  url: string;
  topLineText: string;
  bottomLineText: string;
  textColor: string;
  ratingBadge: string;
} => {
  if (!asset || !asset.attributes.title) {
    return {
      url: "",
      topLineText: "",
      bottomLineText: "",
      textColor: "",
      ratingBadge: "",
    };
  }
  const assetTitle = asset.attributes.title;

  // First try exact match
  let clipInfo = CLIPS_INFO[assetTitle as keyof typeof CLIPS_INFO];

  // If no exact match, try partial match
  if (!clipInfo) {
    const matchingKey = Object.keys(CLIPS_INFO).find((key) => {
      const titleKey = key.toLowerCase();
      const title = assetTitle.toLowerCase();

      // Check for Real Housewives variants (RHOSLC, RHOBH, etc.)
      if (title.startsWith("rho")) {
        clipInfo = CLIPS_INFO["Housewives"];
      }

      return titleKey.includes(title) || title.includes(titleKey);
    });

    if (matchingKey) {
      clipInfo = CLIPS_INFO[matchingKey as keyof typeof CLIPS_INFO];
    }
  }

  // Use asset ID to deterministically pick a fallback clip and copy
  const hash = asset.id
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const clipIndex = hash % DEFAULT_CLIPS.length;
  const copyIndex = hash % DEFAULT_COPY.length;

  const fallbackClipUrl = DEFAULT_CLIPS[clipIndex];
  const fallbackCopy = DEFAULT_COPY[copyIndex];

  const url = clipInfo?.url || fallbackClipUrl;
  const topLineText = clipInfo?.topLineText || fallbackCopy[0];
  const bottomLineText = clipInfo?.bottomLineText || fallbackCopy[1];
  const textColor = clipInfo?.textColor || "#FF7112";
  const ratingBadge = clipInfo?.ratingBadge || "TVPG";

  return {
    url,
    topLineText,
    bottomLineText,
    textColor,
    ratingBadge,
  };
};

export const getNBAClip = (index: number): string => NBA_CLIPS[index] || "";
